import axios from "axios";
import {
  getHttpProtocol as scheme,
  getUserServiceUrl as userServiceUrl,
} from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { getProfilePayload } from "../pages/Users/helpers";
import type { UpdateProfileData } from "../pages/Users/types";

// custom axios
const userAxios = axios.create({
  baseURL: `${scheme}//${userServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

userAxios.interceptors.request.use(requestAccessTokenInterceptor);

userAxios.interceptors.request.use(refreshAccessTokenInterceptor);

userAxios.interceptors.response.use(responseStatusInterceptor);

// entities types
export interface Member {
  name: string;
  value: string;
  id: string;
  user_id: number;
  client_uuid: string;
  first_name: string;
  last_name: string;
  phone: string;
  nickname: string;
  user_type_id: number;
  is_lead: number;
  is_member: number;
  email: string;
  uuid: string;
  active: number;
  last_checkin_timestamp: number;
  membership_status: string;
  user_uuid: string;
  image_url: string;
  primary_image_key: string;
  default_billing_id?: string;
  billing_last_four?: string;
  billable: string;
  subscription_count: number;
  headshot: string;
}

export type SubAccount = {
  clientUuid: string;
  email: string;
  firstName: string;
  lastName: string;
  userUuid: string;
};

export interface V2Member {
  activatedAt: string;
  active: boolean;
  address1?: string;
  address2?: string;
  archived: boolean;
  assignedToUserId?: number;
  assignedToUserUuid?: string;
  blockedAt?: string | null;
  city?: string;
  clientUserId: number;
  clientUuid: string;
  code: string;
  country?: string;
  createdAt: string;
  deleted: boolean;
  display: string;
  dob?: string;
  email: string;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
  externalId?: number | null;
  externalProfileUrl: string;
  firstCheckinTimestamp?: number | null;
  firstName: string;
  gender?: string;
  guardianEmail?: string;
  guardianName?: string;
  guardianPhone?: string;
  isLead: boolean;
  isMember: boolean;
  isStaff: boolean;
  joinedAt: string;
  lastCheckedInAt: string;
  lastName: string;
  linkedAccounts?: SubAccount[];
  membershipPaused: number;
  membershipRole: string;
  membershipStatus: string;
  memberSince?: string; // returned as ISO string, saved as a 10 digit timestamp in DB
  nickname: string;
  objective?: string;
  parentUserId: number;
  parentUserUuid?: string;
  paused: boolean;
  phone: string;
  postalCode?: string;
  primaryImage: string | null;
  referredById?: number | null;
  referredByOther?: string | null;
  referredByUserId?: number | null;
  role: string;
  state?: string;
  status: string;
  updatedAt: string;
  username: string;
  userId: number;
  userUuid: string;
  uuid: string; // clientUserUuid
}

export type QuickActionType =
  | "link"
  | "legacy_link"
  | "external_link"
  | "callback";

export type QuickActionValue =
  | "REPORT_PAYMENTS_FORECAST"
  | "REPORT_FINANCIAL"
  | "REPORT_NO_SHOW"
  | "REPORT_ENGAGEMENT"
  | "REPORT_CELEBRATIONS"
  | "MEMBERS"
  | "LEADS"
  | "STAFF"
  | "CHECKIN_SIDE_PANEL";

export type QuickActionRole =
  | "superuser"
  | "admin"
  | "coach"
  | "frontdesk"
  | "member"
  | "non-member"
  | "ex-member"
  | "lead";
export interface QuickAction {
  uuid: string;
  value: QuickActionValue;
  label: string;
  action: string;
  description: string;
  type: QuickActionType;
  role: QuickActionRole;
  position: number;
  isDefault?: boolean;
}

type QuickActionBatchUpdateResult = {
  action: string;
  status: string;
  result: { numUpdated: string | number; numCreated: string | number }[];
  normalizedPosition: number;
};

export type QuickActionBatchUpdateResponse = {
  data: string;
  count: number;
  results: QuickActionBatchUpdateResult[];
};

export type DatePresetType =
  | "today"
  | "yesterday"
  | "week_to_date"
  | "month_to_date"
  | "quarter_to_date"
  | "year_to_date"
  | "last_7_days"
  | "last_30_days";

export interface PinnedDateSettings {
  startDate?: string;
  endDate?: string;
  preset?: DatePresetType;
}

export interface UserDocument {
  uuid: string;
  active: boolean;
  create_timestamp: number;
  doc: {
    title: string;
    content: string;
    uuid: string;
    version: number;
  };
  document_id: number;
  document_url: string;
  is_checked: boolean;
  is_signed: boolean;
  is_verified: boolean;
  pdf_generated: boolean;
  signature_data: string;
  signature_source: string;
  signed_by_first_name: string;
  signed_by_last_name: string;
  signed_timestamp: number;
  subscription_id: number;
  version: number;
}

export type UserDocumentResponse = {
  wasSuccessful: boolean;
  data: {
    page: number;
    limit: number;
    hasMore: boolean;
    total: number;
    data: UserDocument[];
  };
};

export type ClientDocumentResponse = {
  wasSuccessful: boolean;
  data: {
    page: number;
    limit: number;
    hasMore: boolean;
    total: number;
    data: ClientDocument[];
  };
};

export interface ClientDocument {
  uuid: string;
  active: boolean;
  attach_to: "person" | "plan" | "discount";
  client_id: number;
  content: string;
  create_timestamp: number;
  deleted: boolean;
  id: number;
  is_parent: boolean;
  parent_id: number;
  title: string;
  update_timestamp: number;
  version: number;
}

export type CreateNoteBody = {
  note: string;
  highPriority: boolean;
};

export type UserNote = {
  cleared: boolean;
  clientId: number;
  clientUserId: number;
  clientUuid: string;
  copyMember: boolean;
  createTimestamp: number;
  createdBy: number;
  createdByFirstName: string;
  createdByLastName: string;
  deleted: boolean;
  highPriorityNote: boolean;
  id: number;
  note: string;
  reminderId: string;
  reminderSent: boolean;
  reminderSentTimestamp: number;
  reminderTimestamp: number;
  remindOnCheckin: boolean;
  remindViaEmail: boolean;
  remindViaSlack: boolean;
  remindViaSms: boolean;
  smsNumber: number;
  userId: number;
  userUuid: string;
  uuid: string;
};

export type LeadFunnel = {
  clientId: number;
  clientUserId: number;
  createdByFirstName: string;
  createdByLastName: string;
  id: number;
  leadStatus: string;
  notes: string;
  operatorId: number;
  timestamp: number;
  updatedAtTimestamp: string;
};

export interface UserAndLeadNotesResponse {
  userNotes: UserNote[];
  leadFunnel: LeadFunnel[];
}

// endpoints
export type GetMembersResult = Member[];

export async function getMembers(clientId: string, query: string) {
  const url = `/checkin/search/${query.trim()}/?source=CP&clientId=${clientId}`;
  const response = await userAxios.get<GetMembersResult>(url);
  return response.data;
}

export type GetMemberResult = V2Member | null;

export async function getUserProfileByUserUuid(
  userUuid: string,
  clientUuid: string,
): Promise<GetMemberResult> {
  const url = `/profiles?user_uuid=${userUuid}&client_uuid=${clientUuid}&linkedAccounts=1`;
  const response = await userAxios.get<{ data: V2Member[] }>(url);
  const data = response.data;

  if (data.data.length > 0) {
    return data.data[0];
  }
  return null;
}

export async function updateClientUserProfile(
  clientUserUuid: string,
  data: UpdateProfileData[],
) {
  const response = await userAxios.patch<{ data: V2Member }>(
    `/profile/${clientUserUuid}`,
    getProfilePayload(data),
  );
  return response.data;
}

export async function blockUser(userUuid: string) {
  const response = await userAxios.post(`/${userUuid}/block`, {
    blockedAt: new Date().toISOString(),
  });
  return response.data;
}

export async function unblockUser(userUuid: string) {
  const response = await userAxios.post(`/${userUuid}/unblock`, {
    blockedAt: null,
  });
  return response.data;
}

export async function patchUser(
  userUuid: string,
  data: { parentUserId?: number; email?: string },
) {
  const response = await userAxios.patch(`/update-user/${userUuid}`, data);
  return response.data;
}

export async function getQuickActions(): Promise<QuickAction[]> {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.get<{ data: QuickAction[] }>(url);
  return response.data.data;
}

export async function getQuickActionsList(): Promise<QuickAction[]> {
  const url = `/preferences/quick-actions/list`;
  const response = await userAxios.get<{ data: QuickAction[] }>(url);
  return response.data.data;
}

export interface ToggleQuickActionParams {
  uuid: string;
  operation: "add" | "remove";
}

export async function toggleQuickAction(
  params: ToggleQuickActionParams,
): Promise<{ data: string }> {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.post<{ data: string }>(url, params);
  return response.data;
}

export interface UpdateQuickActionPositionsParams {
  actions: Array<{ uuid: string; position: number }>;
}

export async function updateQuickActionPositions(
  params: UpdateQuickActionPositionsParams,
): Promise<QuickActionBatchUpdateResponse> {
  const url = `/preferences/quick-actions/batch`;
  const response = await userAxios.patch<QuickActionBatchUpdateResponse>(
    url,
    params,
  );
  return response.data;
}

export async function resetQuickActions() {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.delete<{ data: string }>(url);
  return response.data;
}

export async function pinDateSettings(
  settings: PinnedDateSettings,
): Promise<void> {
  const url = `/preferences/pinned-date`;
  await userAxios.post(url, settings);
}

export async function getPinnedDateSettings(): Promise<PinnedDateSettings> {
  const url = `/preferences/pinned-date`;
  const response = await userAxios.get<{ data: PinnedDateSettings }>(url);
  return response.data.data;
}

export async function getUserDocuments(
  clientUuid: string,
  userUuid: string,
): Promise<UserDocument[]> {
  const url = `/documents/${clientUuid}/${userUuid}`;
  const response = await userAxios.get<{ data: { data: UserDocument[] } }>(url);
  return response.data.data.data;
}

export async function getClientDocuments(
  clientUuid: string,
): Promise<ClientDocument[]> {
  const url = `/documents/${clientUuid}`;
  const response = await userAxios.get<{ data: ClientDocument[] }>(url);
  return response.data.data;
}

export type CreateUserDocumentBody = {
  documentUuid: string;
  subscriptionUuid: string;
};

export async function createUserDocument(
  userUuid: string,
  clientUuid: string,
  data: CreateUserDocumentBody,
) {
  const url = `/documents/${clientUuid}/${userUuid}`;
  const response = await userAxios.post<{ data: CreateUserDocumentBody }>(url, {
    document_id: data.documentUuid,
    subscription_uuid: data.subscriptionUuid,
  });
  return response.data;
}

export async function revokeUserDocument(userDocumentId: string) {
  const url = `/documents/user/${userDocumentId}/revoke`;
  const response = await userAxios.post(url, {
    user_document_id: userDocumentId,
  });
  return response.data;
}

export async function getUserAndLeadNotes(
  clientUuid: string,
  userUuid: string,
): Promise<UserAndLeadNotesResponse> {
  const url = `/notes/${clientUuid}/${userUuid}`;
  const response = await userAxios.get(url);
  return response.data;
}

export async function createUserNote(userUuid: string, note: CreateNoteBody) {
  const url = `/${userUuid}/note`;
  const response = await userAxios.post(url, note);
  return response.data;
}

export async function deleteNote(noteUuid: string) {
  const url = `/note/${noteUuid}`;
  const response = await userAxios.delete(url);
  return response.data;
}
